import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosConfig";
import "./Products.scss";
import { toast } from "react-toastify";
import LoadingScreen from "../../Components/LoadingScreen/LoadingScreen";
import Select from "react-select"; // Import react-select

const Products = () => {
  // --- STATE MANAGEMENT ---
  const [products, setProducts] = useState([]);
  const [authorityOptions, setAuthorityOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Unified form data state with new fields
  const [formData, setFormData] = useState({
    productName: "",
    productCode: "",
    productStatus: "ACTIVE",
    productType: "SAAS", // New field
    productLink: "",
    productIconUrl: "", // New field
    description: "",
    authorityIds: [],
  });

  const navigate = useNavigate();

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Assuming endpoints for admin and hrm are the same '/products'
        // If they are different, you might need logic to switch between them
        const [productsResponse, authoritiesResponse] = await Promise.all([
          axiosInstance.get("/products"), // This fetches for admin/hrm
          axiosInstance.get("/authority"),
        ]);

        if (productsResponse.data.status === 200) {
          setProducts(productsResponse.data.data);
        } else {
          setError(productsResponse.data.message || "Failed to fetch products");
        }

        if (authoritiesResponse.data.status === 200) {
          // Transform the API data into the format react-select needs ({ value, label })
          // Updated to handle the new authority endpoint structure
          const options = authoritiesResponse.data.data.map((auth) => ({
            value: auth.authorityId,
            label: auth.authorityName,
          }));
          setAuthorityOptions(options);
        } else {
          console.error("Failed to fetch authorities");
        }
      } catch (err) {
        setError("An error occurred while fetching data.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // --- EVENT HANDLERS ---
  const handleProductSelect = (product) => {
    localStorage.setItem("selectedProduct", JSON.stringify(product));
    navigate(`/home/software-update/modules`, {
      state: { selectedProduct: product },
    });
  };

  const resetFormData = () => {
    setFormData({
      productName: "",
      productCode: "",
      productStatus: "ACTIVE",
      productType: "SAAS", // Reset new field
      productLink: "",
      productIconUrl: "", // Reset new field
      description: "",
      authorityIds: [],
    });
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    resetFormData();
    setShowModal(true);
  };

  const handleEditProduct = (product, e) => {
    e.stopPropagation();
    setEditingProduct(product);

    setFormData({
      productName: product.productName || "",
      productCode: product.productCode || "",
      productStatus: product.productStatus || "ACTIVE",
      productType: product.productType || "SAAS",
      productLink: product.productLink || "",
      productIconUrl: product.productIconUrl || "",
      description: product.description || "",

      // FIX HERE
      authorityIds: product.authorities
        ? product.authorities.map((auth) => auth.authorityId)
        : [],
    });

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    resetFormData();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAuthorityChange = (selectedOptions) => {
    const selectedIds = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setFormData((prev) => ({
      ...prev,
      authorityIds: selectedIds,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    // Payload now includes productType and productIconUrl from formData
    const payload = { ...formData };

    try {
      let response;
      if (editingProduct) {
        response = await axiosInstance.put(
          `/products/${editingProduct.productId}`,
          payload,
        );
      } else {
        response = await axiosInstance.post("/products", payload);
      }

      if (response.data.status === 200 || response.status === 200) {
        setShowModal(false);
        // Refresh product list after successful creation/update
        const productsResponse = await axiosInstance.get("/products");
        const normalizedProducts = productsResponse.data.data.map((p) => ({
          ...p,
          authorityIds: p.authorities
            ? p.authorities.map((a) => a.authorityId)
            : [],
        }));

        setProducts(normalizedProducts);
        toast.success(
          `Product ${editingProduct ? "updated" : "created"} successfully!`,
        );
      } else {
        toast.error(
          response.data.message ||
            `Failed to ${editingProduct ? "update" : "create"} product.`,
        );
      }
    } catch (err) {
      toast.error(
        `An error occurred while ${
          editingProduct ? "updating" : "creating"
        } the product.`,
      );
      console.error("Form submission error:", err);
    } finally {
      setFormLoading(false);
    }
  };

  // --- DELETE LOGIC ---
  const handleDeleteProduct = (product, e) => {
    e.stopPropagation();
    setDeletingProduct(product);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingProduct) return;
    setDeleteLoading(true);
    try {
      const response = await axiosInstance.delete(
        `/products/${deletingProduct.productId}`,
      );
      if (response.data.status === 200 || response.status === 200) {
        setShowDeleteModal(false);
        setDeletingProduct(null);
        const productsResponse = await axiosInstance.get("/products");
        setProducts(productsResponse.data.data);
        toast.success("Product deleted successfully!");
      } else {
        toast.error(response.data.message || "Failed to delete product");
      }
    } catch (err) {
      toast.error("Error deleting product");
      console.error("Error deleting product:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingProduct(null);
  };

  // --- UTILITY ---
  const getStatusColor = (status) => {
    // Using the specified enum values
    switch (status?.toUpperCase()) {
      case "ACTIVE":
        return "#017bfd";
      case "IN_ACTIVE":
        return "#f44336";
      case "MAINTENANCE":
        return "#ff9800";
      default:
        return "#2196F3";
    }
  };

  // --- RENDER LOGIC ---
  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <div className="products-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-btn"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="products-header">
        <h1>Select a Product</h1>
        <p>Choose a product to manage its configuration and settings</p>
        <button className="add-product-btn" onClick={handleAddProduct}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add New Product
        </button>
      </div>

      <div className="products-grid">
        {products.map((product) => (
          <div
            key={product.productId}
            className="product-card"
            onClick={() => handleProductSelect(product)}
          >
            <div className="card-header">
              <div className="product-title-container">
                {/* Display Product Icon */}
                {product.productIconUrl && (
                  <img
                    src={product.productIconUrl}
                    alt={`${product.productName} icon`}
                    className="product-icon"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }} // Hide if image fails to load
                  />
                )}
                <h3 className="product-name">{product.productName}</h3>
              </div>
              <div className="header-actions">
                <span
                  className="product-status"
                  style={{
                    backgroundColor: getStatusColor(product.productStatus),
                  }}
                >
                  {product.productStatus || "Unknown"}
                </span>
                <button
                  className="edit-btn"
                  onClick={(e) => handleEditProduct(product, e)}
                  title="Edit Product"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  className="delete-btn"
                  onClick={(e) => handleDeleteProduct(product, e)}
                  title="Delete Product"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="3,6 5,6 21,6"></polyline>
                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="product-meta">
                <div className="product-code">
                  <strong>Code:</strong> {product.productCode}
                </div>
                {/* Display Product Type */}
                <div className="product-type">
                  <strong>Type:</strong> {product.productType}
                </div>
              </div>
              {product.description && (
                <div className="product-description">
                  <p>{product.description}</p>
                </div>
              )}
            </div>
            <div className="card-footer">
              <button className="select-btn">
                Select Product
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="no-products">
          <h2>No Products Found</h2>
          <p>There are no products available at the moment.</p>
          <button className="add-first-product-btn" onClick={handleAddProduct}>
            Add Your First Product
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingProduct ? "Edit Product" : "Add New Product"}</h2>
              <button className="close-btn" onClick={handleCloseModal}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h3>Product Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="productName">Product Name *</label>
                    <input
                      type="text"
                      id="productName"
                      name="productName"
                      value={formData.productName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="productCode">Product Code *</label>
                    <input
                      type="text"
                      id="productCode"
                      name="productCode"
                      value={formData.productCode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="productStatus">Product Status</label>
                    <select
                      id="productStatus"
                      name="productStatus"
                      value={formData.productStatus}
                      onChange={handleInputChange}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="IN_ACTIVE">IN_ACTIVE</option>
                      <option value="MAINTENANCE">MAINTENANCE</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="productType">Product Type</label>
                    <select
                      id="productType"
                      name="productType"
                      value={formData.productType}
                      onChange={handleInputChange}
                    >
                      <option value="SAAS">SAAS</option>
                      <option value="PUBLIC_TOOL">PUBLIC_TOOL</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="productLink">Product Link</label>
                    <input
                      type="text"
                      id="productLink"
                      name="productLink"
                      value={formData.productLink}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="productIconUrl">Product Icon URL</label>
                    <input
                      type="url"
                      id="productIconUrl"
                      name="productIconUrl"
                      value={formData.productIconUrl}
                      onChange={handleInputChange}
                      placeholder="Paste the iconify icon name"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="authorityIds">Authorities</label>
                  <Select
                    id="authorityIds"
                    isMulti
                    options={authorityOptions}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    value={authorityOptions.filter((option) =>
                      formData.authorityIds.includes(option.value),
                    )}
                    onChange={handleAuthorityChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={formLoading}
                >
                  {formLoading ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      {editingProduct ? "Updating..." : "Creating..."}
                    </>
                  ) : editingProduct ? (
                    "Update Product"
                  ) : (
                    "Create Product"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h2>Confirm Deletion</h2>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the product "
                <strong>{deletingProduct?.productName}</strong>"? This action
                cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={handleCloseDeleteModal}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-delete-btn"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <div className="loading-spinner-small"></div>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
